// Client for the Ask dock's server API (backend/workflows/assistant/*).
import { assistantRequest } from './dataApi';

export interface AskResource {
  type: string;
  id: string;
  name?: string;
  url?: string;
}
export interface AskEvidence {
  label: string;
  url?: string;
}
export interface AskAction {
  kind: 'open' | 'copy' | 'external' | 'reply';
  label: string;
  url: string;
  // reply: sent as the user's next message when the button is tapped.
  text?: string;
}
export interface AskSnapshot {
  route: string;
  tab?: string;
  channel?: string;
  channelTitle?: string;
  visibility?: string;
  owner: boolean;
  source?: string;
  conflict?: string[];
}
export interface AskApproval {
  id: string;
  kind: string;
  status: 'pending' | 'approved' | 'denied' | 'applied' | 'failed';
  channel: string;
  publicName: string;
  title: string;
  discloses: string[];
  never: string[];
  warnings?: string[];
  ops?: { op: string; target?: string; set: Record<string, unknown> }[];
  url?: string;
  error?: string;
}
export interface AskEvent {
  seq: number;
  conversationId: string;
  executionId?: string;
  timestamp: string;
  kind: 'message' | 'step' | 'approval' | 'result';
  role?: 'user' | 'assistant';
  text?: string;
  stepId?: string;
  stage?: string;
  status?: string;
  summary?: string;
  resource?: AskResource;
  evidence?: AskEvidence[];
  context?: AskSnapshot;
  approval?: AskApproval;
  actions?: AskAction[];
  data?: Record<string, unknown>;
}
export interface AskConversation {
  id: string;
  title: string;
  activeChannel?: string;
  updatedAt: string;
}
export interface AskActive {
  id: string;
  status: string;
  stage?: string;
  summary?: string;
  channel?: string;
}
export interface AskState {
  conversation: AskConversation;
  events: AskEvent[];
  lastSeq: number;
  active?: AskActive | null;
}
export interface AskWireContext {
  route: string;
  channel?: string;
  tab?: string;
  reportNumber?: string;
  pinned?: string;
}
export interface AskSendResult {
  conversationId: string;
  seq: number;
  executionId?: string;
  approvalId?: string;
  replayed?: boolean;
}

type Ok<T> = { ok: true; data: T } | { ok: false; error: string; status?: number };

async function call<T>(path: string, method: string, body?: unknown): Promise<Ok<T>> {
  const res = await assistantRequest<T>(path, method, body);
  return res.success ? { ok: true, data: res.data as T } : { ok: false, error: res.error ?? 'Request failed', status: res.status };
}

export const assistantApi = {
  list: () => call<{ conversations: AskConversation[] }>('/assistant/conversations', 'GET'),
  events: (id: string, after: number) => call<AskState>(`/assistant/conversations/${encodeURIComponent(id)}/events?after_seq=${after}`, 'GET'),
  // `ai` is the selected provider and, when this browser has it, the person's own key from Settings -> AI, sent with this message only (never stored server-side). Without a key the server uses the one saved on the account.
  send: (id: string | null, text: string, context: AskWireContext, idempotencyKey: string, ai?: { provider: string; apiKey?: string }) =>
    call<{ result: AskSendResult }>(`/assistant/conversations/${id ? encodeURIComponent(id) : 'new'}/messages`, 'POST', { text, context, idempotencyKey, ...(ai ? { ai } : {}) }),
  decide: (approvalId: string, decision: 'approve' | 'deny') => call<{ approval: AskApproval }>(`/assistant/approvals/${encodeURIComponent(approvalId)}`, 'POST', { decision }),
  cancel: (execId: string) => call<unknown>(`/assistant/executions/${encodeURIComponent(execId)}/cancel`, 'POST', {}),
};
